/**
 * The month close, shared by the committee phone app (its read-only status)
 * and the desk (the close itself): readings left, duplicate bills, and the
 * WhatsApp log (docs/changes/43).
 */

type MonthMeter<B> = {
  id: string;
  label: string | null;
  member: { id: string; fullName: string | null } | null;
  monthBills: { nodes: (B | null)[] };
};

export type MeterReadings<B> = {
  id: string;
  label: string;
  memberId: string | null;
  memberName: string;
  bills: B[];
};

/** Connected meters split into not read this month / read twice (duplicates). */
export function readingsStatus<B>(nodes: (MonthMeter<B> | null)[]) {
  const meters: MeterReadings<B>[] = nodes.flatMap((node) =>
    node
      ? [
          {
            id: node.id,
            label: node.label ?? '',
            memberId: node.member?.id ?? null,
            memberName: node.member?.fullName ?? '',
            bills: node.monthBills.nodes.filter((b): b is B => b !== null),
          },
        ]
      : [],
  );
  return {
    total: meters.length,
    unread: meters.filter((m) => m.bills.length === 0),
    duplicates: meters.filter((m) => m.bills.length > 1),
  };
}

export type WhatsappStatus =
  | 'QUEUED'
  | 'SENT'
  | 'DELIVERED'
  | 'READ'
  | 'FAILED';

/**
 * One line per bill: its latest message (a bill re-sent after a failure has
 * several). `messages` must be newest first.
 */
export function latestPerBill<M extends { id: string; billId: string | null }>(
  messages: (M | null)[],
): M[] {
  const seen = new Set<string>();
  return messages.filter((m): m is M => {
    if (!m) return false;
    const key = m.billId ?? m.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function countByStatus(messages: { status: WhatsappStatus }[]) {
  const counts: Record<WhatsappStatus, number> = {
    QUEUED: 0,
    SENT: 0,
    DELIVERED: 0,
    READ: 0,
    FAILED: 0,
  };
  for (const m of messages) counts[m.status] += 1;
  return counts;
}

export const WHATSAPP_STATUS_LABELS: Record<WhatsappStatus, string> = {
  QUEUED: 'في الانتظار',
  SENT: 'أُرسلت',
  DELIVERED: 'وصلت',
  READ: 'قُرئت',
  FAILED: 'فشلت',
};

export const WHATSAPP_STATUS_COLORS: Record<WhatsappStatus, string> = {
  QUEUED: 'default',
  SENT: 'blue',
  DELIVERED: 'cyan',
  READ: 'green',
  FAILED: 'red',
};

/** arqam's sending errors, in Arabic. */
export const WHATSAPP_ERRORS = {
  WHATSAPP_NO_USABLE_NUMBER: 'ليس لهذا العضو رقم واتساب صالح. صحّح رقمه أولاً.',
  BILL_NOT_FOUND: 'هذه الفاتورة لم تعد موجودة.',
};

/** Consumptions above this are checked before invoices go out (m³). */
export const HIGH_CONSUMPTION = 50;
